<template src="./HistoricalChart.html" />
<script>
import { Chart } from 'highcharts-vue';
import StockChartResource from '../../../../resources/StockChartResource';
import dayjs from 'dayjs';

const CHART_STYLE = {
  fontFamily: '"Noto Sans Japanese", sans-serif',
};

/*
 * 履歴チャートコンポーネント(IR版)
 *
 * @module Stocks/IR/HistoricalChart
 */
export default {
  components: {
    highcharts: Chart,
  },
  props: {
    /**
     * 金融アイテムコード
     * @type {String}
     */
    code: {
      type: String,
      required: true,
    },
    /**
     * 市場ID
     * @type {Number}
     */
    exchangeId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      /**
       * チャートデータ
       * @type {StockChart}
       */
      stockChart: null,
      /**
       * プロットの情報
       * @type {Object}
       */
      plotData: {},
    };
  },
  computed: {
    /**
     * チャートオプション
     * @return {Object}
     */
    chartOptions() {
      return {
        chart: {
          height: '70px',
          margin: [0, 0, 0, 0],
          panning: false,
          backgroundColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, 'rgb(5, 66, 114)'], // top
              [1, 'rgb(5, 56, 99)'], // bottom slightly darker
            ],
          },
          plotBackgroundColor: 'rgb(5, 66, 114)',
          animation: false,
          style: {
            fontFamily: CHART_STYLE.fontFamily,
          },
        },
        tooltip: {
          enabled: false,
          shared: false,
          split: false,
        },
        legend: {
          enabled: true,
          floating: false,
          align: 'left',
          verticalAlign: 'bottom',
          itemStyle: {
            color: '#333',
            fontWeight: 'normal',
            fontSize: 10,
          },
          x: -16,
          y: 20,
        },
        title: {
          text: '',
        },
        credits: {
          enabled: false,
        },
        rangeSelector: {
          enabled: false,
        },
        navigator: {
          enabled: false,
        },
        scrollbar: {
          enabled: false,
        },
        plotOptions: {
          area: {
            // 見本に近いシアン系の線色
            color: '#6ED3FF',
            lineColor: '#6ED3FF',
            lineWidth: 1,
            marker: {
              symbol: 'circle',
              radius: 1.5,
              fillColor: '#6ED3FF',
              lineColor: '#6ED3FF',
            },
          },
        },
        series: [
          {
            type: 'area',
            name: '株価',
            data: this.chartData,
            animation: false,
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, 'rgba(110, 211, 255, 0.35)'],
                [1, 'rgba(110, 211, 255, 0.0)'],
              ],
            },
            color: '#6ED3FF',
            lineColor: '#6ED3FF',
            states: {
              inactive: {
                enabled: false,
              },
              hover: {
                enabled: false,
              },
            },
            showInLegend: false,
          },
        ],
        xAxis: [
          {
            tickLength: 0,
            startOnTick: false,
            endOnTick: false,
            showFirstLabel: true,
            showLastLabel: true,
            labels: { enabled: false },
            lineWidth: 0,
            dateTimeLabelFormats: {
              day: '%m/%d',
              week: '%m/%d',
              month: '%Y/%m',
              year: '%Y',
            },
            max: this.xAxisMax,
            min: this.xAxisMin,
            crosshair: false,
          },
        ],
        yAxis: [
          {
            showFirstLabel: false,
            showLastLabel: false,
            gridLineColor: '#ddd',
            gridLineWidth: 0,
            minorGridLineWidth: 0,
            lineWidth: 0,
            crosshair: false,
            opposite: true,
            max: this.yAxisMax,
            min: this.yAxisMin,
            // 軸ラベルを非表示にするため、オフセットも0
            offset: 0,
            minTickInterval: 1,
            labels: { enabled: false },
          },
        ],
      };
    },
    /**
     * チャート描画用データ
     * @return {Array}
     */
    chartData() {
      if (!this.stockChart) {
        return [];
      }

      return this.stockChart.chartData(this.barType);
    },
    /**
     * 表示するチャートの足の種類
     * @return {String}
     */
    barType() {
      return 'daily';
    },
    /**
     * 表示されている分のチャートデータ
     * @return {Array}
     */
    displayChartData() {
      const xMax = this.latestPriceDate;
      const xMin = this.startPriceDate;
      return this.chartData.filter((e) => {
        return xMin <= e[0] && e[0] <= xMax;
      });
    },
    /**
     * Y軸の補正値(最大)
     * @return {Number}
     */
    yAxisMax() {
      const values = this.displayChartData
        .map((e) => e[1])
        .filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
      if (values.length === 0) return undefined;
      return Math.max(...values) * 1.001;
    },
    /**
     * Y軸の補正値(最小)
     * @return {Number}
     */
    yAxisMin() {
      const values = this.displayChartData
        .map((e) => e[1])
        .filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
      if (values.length === 0) return undefined;
      return Math.min(...values) * 0.9995;
    },
    /**
     * X軸の最大値
     * @return {Number}
     */
    xAxisMax() {
      const xs = this.displayChartData.map((e) => e[0]).filter((v) => Number.isFinite(v));
      if (xs.length === 0) return undefined;
      return Math.max(...xs);
    },
    /**
     * X軸の最小値
     * @return {Number}
     */
    xAxisMin() {
      const xs = this.displayChartData.map((e) => e[0]).filter((v) => Number.isFinite(v));
      if (xs.length === 0) return undefined;
      return Math.min(...xs);
    },
    /**
     * 最新の取引日
     * @return {Number}
     */
    latestPriceDate() {
      if (this.chartData.length === 0) return;

      return this.chartData[this.chartData.length - 1][0];
    },
    /**
     * X軸の描画開始日
     * @return {Number}
     */
    startPriceDate() {
      // 取り敢えず固定で3ヶ月分の期間を指定
      const strTerm = 'month';
      const numTerm = 3;

      let day = dayjs(this.latestPriceDate).subtract(numTerm, strTerm);
      return day.valueOf();
    },
    /**
     * Y軸のラベルの幅
     * @return {Number}
     */
    yAxisLabelWidth() {
      const pitch = 7.25;
      const margin = 4;
      const pitchDelimiter = 2.65;
      const words = parseInt(this.yAxisMax).toString().length;

      if (words <= 3) {
        return pitch * words + margin;
      } else {
        return pitch * words + margin + pitchDelimiter;
      }
    },
  },
  mounted() {
    this.loadStockChart();
  },
  methods: {
    /**
     * チャートを読み込む
     * @return {void}
     */
    async loadStockChart() {
      if (this.stockChart) {
        return;
      }

      const stockChartResource = new StockChartResource();
      this.stockChart = await stockChartResource.get(this.code, this.exchangeId);
    },
  },
};
</script>
